import NavigationSettings, { emptyNavigation, navigationFields } from './NavigationSettings';
import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import designThemeApi from "../../../services/designThemeApi";

export default function ThemeModal({ show, onClose, theme, onSaved }) {
  const isEdit = !!theme;
  const isDefault = Number(theme?.id) === 1 || (theme?.code || '').toLowerCase() === 'default';

  const [form, setForm] = useState({
    id: "",
    code: "",
    name: "",
    description: "",
    thumbImg: "",
    status: 1,
    webPrimaryColor: "#480243",
  });

  const [headerEnabled, setHeaderEnabled] = useState(false);
  const [headerPath, setHeaderPath] = useState('');
  const [headerFile, setHeaderFile] = useState(null);
  const [headerPreview, setHeaderPreview] = useState('');
  const [navigation,setNavigation] = useState(emptyNavigation);
  const [navigationFiles,setNavigationFiles] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setHeaderEnabled(theme?.mobileHeaderConfig?.enabled === true);
    setHeaderPath(theme?.mobileHeaderConfig?.backgroundImg || '');
    setHeaderFile(null);
    setNavigation(theme?.mobileHeaderConfig?.navigation || emptyNavigation());
    setNavigationFiles({});
    if (theme) {
      setForm({
        id: theme.id,
        code: theme.code || "",
        name: theme.name || "",
        description: theme.description || "",
        thumbImg: theme.thumbImg || "",
        status: theme.status ?? 1,
        webPrimaryColor: theme.webPrimaryColor || "#480243",
      });
    } else {
      setForm({
        id: "",
        code: "",
        name: "",
        description: "",
        thumbImg: "",
        status: 1,
        webPrimaryColor: "#480243",
      });
    }
  }, [theme]);

  useEffect(() => {
    if (!headerFile) { setHeaderPreview(''); return undefined; }
    const url = URL.createObjectURL(headerFile);
    setHeaderPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [headerFile]);

  const imageUrl = headerPreview || (headerPath.startsWith('/uploads/') ? `${process.env.REACT_APP_IMAGE_URL || ''}${headerPath}` : /^https:\/\//i.test(headerPath) ? headerPath : '');

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) {
      alert("Code and Name are required");
      return;
    }

    if (!isDefault && headerEnabled && !headerFile && !headerPath) {
      alert('Upload a mobile header background before enabling it');
      return;
    }
    if (!isDefault && navigation.background?.type === 'image' && !navigation.background.image && !navigationFiles.background) { alert('Upload a navigation background image'); return; }
    if (!/^#[0-9a-f]{6}$/i.test(form.webPrimaryColor)) {
      alert('Enter a valid colour, for example #480243'); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...(isEdit ? { id: form.id } : {}),
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description || null,
        thumbImg: form.thumbImg || null,
        status: parseInt(form.status),
        webPrimaryColor: form.webPrimaryColor,
        mobileNavigationConfig: isDefault ? null : navigation,
        mobileHeaderConfig: isDefault ? null : { enabled: headerEnabled, backgroundImg: headerPath || null },
      };

      let request = payload;
      if (!isDefault && (headerFile || Object.values(navigationFiles).some(Boolean))) {
        request = new FormData();
        Object.entries(payload).forEach(([key, value]) => request.append(key, ['mobileHeaderConfig','mobileNavigationConfig'].includes(key) ? JSON.stringify(value) : value ?? ''));
        if (headerFile) request.append('mobileHeaderBackground', headerFile);
        Object.entries(navigationFiles).forEach(([key,file]) => { if (file) request.append(navigationFields[key],file); });
      }
      const res = await designThemeApi.saveTheme(request);
      if (!res?.success) {
        alert(res?.message || "Failed to save theme");
        return;
      }
      onSaved?.(res.data);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to save theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered className="theme-publishing-modal">
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? "Edit Theme" : "Create Theme"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Theme Code</Form.Label>
            <Form.Control
              name="code"
              value={form.code}
              onChange={onChange}
              placeholder="pongal / diwali / christmas"
              disabled={isEdit} // keep stable key
            />
            <div className="text-muted small mt-1">
              Code should be unique. Example: <code>pongal</code>
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Theme Name</Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Pongal"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              name="description"
              value={form.description}
              onChange={onChange}
              placeholder="Short notes (optional)"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Thumbnail Image Path (optional)</Form.Label>
            <Form.Control
              name="thumbImg"
              value={form.thumbImg}
              onChange={onChange}
              placeholder="/uploads/themes/pongal.png"
            />
          </Form.Group>

          <section className="border rounded p-3 mb-3" aria-label="Website colour settings">
            <Form.Group controlId="web-primary-colour">
              <Form.Label>Website primary colour</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="color" aria-label="Choose website colour" value={/^#[0-9a-f]{6}$/i.test(form.webPrimaryColor) ? form.webPrimaryColor : '#480243'} onChange={event => setForm(previous => ({ ...previous, webPrimaryColor: event.target.value }))} style={{ width: 64 }} />
                <Form.Control name="webPrimaryColor" value={form.webPrimaryColor} onChange={onChange} placeholder="#480243" maxLength={7} required pattern="#[0-9a-fA-F]{6}" />
              </div>
              <div className="rounded p-2 mt-2" style={{ backgroundColor: /^#[0-9a-f]{6}$/i.test(form.webPrimaryColor) ? form.webPrimaryColor : '#480243', color: '#fff' }}>Welcome to Grace · Add to cart</div>
              <p className="text-muted small mt-2 mb-0">Used for website headers, buttons and highlights. Save, then publish this theme for Website → Home. The website uses this colour across its pages. Mobile app colours are unchanged.</p>
            </Form.Group>
          </section>

          <section className="border rounded p-3 mb-3" aria-label="Mobile header settings">
            <h6>Mobile header background</h6>
            {isDefault ? <p className="text-muted mb-0">Default theme uses the normal app header.</p> : <>
              <Form.Check id="mobile-header-enabled" type="switch" label="Show a background image in the mobile header" checked={headerEnabled} onChange={event => setHeaderEnabled(event.target.checked)} />
              <p className="text-muted small mt-2">Mobile Home and Offers only, when this theme is published there. Website headers are unaffected. Recommended: a wide image around 1200 × 600 px, with a dark, clear centre for header controls.</p>
              <Form.Group controlId="mobile-header-file" className="mb-2">
                <Form.Label>Upload header background</Form.Label>
                <Form.Control type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={event => {
                  const file = event.target.files?.[0];
                  if (file && (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
                    alert('Choose a PNG, JPEG, WebP or GIF image up to 5 MB'); event.target.value = ''; return;
                  }
                  setHeaderFile(file || null);
                }} />
              </Form.Group>
              <Form.Group controlId="mobile-header-path" className="mb-2">
                <Form.Label>Or use an existing image path / HTTPS URL</Form.Label>
                <Form.Control value={headerPath} disabled={!!headerFile} onChange={event => setHeaderPath(event.target.value)} placeholder="/uploads/themes/mobile-header.png" />
              </Form.Group>
              {imageUrl && <div className="rounded overflow-hidden mb-2" style={{ background: '#321136', aspectRatio: '2 / 1', position: 'relative' }}>
                <img src={imageUrl} alt="Mobile header background preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <span style={{ position: 'absolute', left: 16, bottom: 16, color: '#fff', textShadow: '0 1px 4px #000' }}>Delivery · Home<br />Search products…</span>
              </div>}
              {(headerPath || headerFile) && <Button size="sm" variant="outline-secondary" onClick={() => { setHeaderFile(null); setHeaderPath(''); setHeaderEnabled(false); }}>Remove background</Button>}
              <div className="text-muted small mt-2">Turning this off restores the normal mobile header and keeps your image for later. Save changes to apply.</div>
            </>}
          </section>

          <NavigationSettings value={navigation} files={navigationFiles} onChange={setNavigation} onFilesChange={setNavigationFiles} isDefault={isDefault} />

          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select name="status" value={form.status} onChange={onChange}>
              <option value={1}>Active</option>
              <option value={2}>Inactive</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

